import { createSelector } from '@reduxjs/toolkit';
import { DefaultRootState } from 'react-redux';
import { EPlatformsNames, EStripeSubStatus, ETrialStatus } from '../../../models/consts';
import { EClaudePlanNames } from '../../../models/paymentPlans/claude-payments-plan';
import { IPlatformSubscriptionRow } from '../../../views/UserPages/Subscription/Subscription';
import { PlatformsState } from '../../reducers/platform';

export const getClaudeReposSelector = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.items);
export const getClaudeTotal = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.totalItems);

export const getClaudeCanUserBackup = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.canUserBackup);

export const isClaudeRepoLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.itemsLoading);

export const isClaudeUsersLoadingSelector = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.usersLoading);

export const getClaudeUsersSelector = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.users);
export const isClaudeConnect = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.isConnectionActive);

export const claudeTrialStatus = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.trial?.trialStatus);

export const claudeTrialHoursLeft = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => {
  return claude.trial?.trialHoursLeft || null;
});

export const claudeTrialQuantityLeft = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => {
  return claude.trial?.availableItemsQuantity || null;
});

export const claudeTrialPlanQuantity = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => {
  return claude.trial?.planQuantity || 0;
});

export const claudeSubInfo = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { subscription } = claude;
  if (subscription) {
    return {
      id: 7,
      platformName: EPlatformsNames.CLAUDE,
      plan: subscription?.planName || '',
      price: `$${subscription?.cost.toFixed(2)} / ${subscription?.isAnnual ? 'yr.' : 'mo.'}`,
      status: subscription?.status,
      quantity: subscription.quantity,
      planQuantity: subscription.planQuantity,
      addedItemsQuantity: subscription.addedItemsQuantity,
      isCancel: subscription.isCancel,
      isConnect: false
    };
  }
  return null;
});

export const claudeTrialInfo = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState): IPlatformSubscriptionRow | null => {
  const { trial } = claude;
  if (trial) {
    return {
      id: 7,
      platformName: EPlatformsNames.CLAUDE,
      plan: EClaudePlanNames.TRIAL,
      price: `-`,
      status: trial.trialStatus,
      quantity: 0,
      planQuantity: 0,
      addedItemsQuantity: 0,
      isCancel: false,
      isConnect: false
    };
  }
  return null;
});

export const claudeSubscription = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => claude.subscription);

export const claudeTrialData = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => {
  return claude.trial
});

export const claudeActualPlan = createSelector((state: DefaultRootState) => state.platform, ({ claude }: PlatformsState) => {
  const { subscription: claudeSub, trial: claudeTrial, isConnectionActive } = claude;

  const claudeItem: IPlatformSubscriptionRow = {
    id: 8,
    platformName: EPlatformsNames.CLAUDE,
    plan: '-',
    price: '-',
    quantity: 0,
    planQuantity: 0,
    addedItemsQuantity: claudeTrial?.addedItemsQuantity || 0,
    status: EStripeSubStatus.INACTIVE,
    isCancel: false,
    finishDateInMillisecond: null,
    isConnect: isConnectionActive
  };

  if (claudeTrial && claudeTrial.trialStatus === ETrialStatus.NOT_STARTED) {
    claudeItem.plan = EClaudePlanNames.TRIAL;
    claudeItem.status = claudeTrial.trialStatus;
    claudeItem.quantity = claudeTrial.availableItemsQuantity || 0;
    claudeItem.addedItemsQuantity = claudeTrial.addedItemsQuantity || 0;
    claudeItem.planQuantity = claudeTrial.planQuantity || 0;
    claudeItem.finishDate = claudeTrial.finishDate || null;
    claudeItem.isConnect = isConnectionActive
  }

  if (claudeTrial && claudeTrial.trialStatus === ETrialStatus.STARTED) {
    claudeItem.plan = EClaudePlanNames.TRIAL;
    claudeItem.status = claudeTrial.trialStatus;
    claudeItem.quantity = claudeTrial.availableItemsQuantity || 0;
    claudeItem.addedItemsQuantity = claudeTrial.addedItemsQuantity || 0;
    claudeItem.planQuantity = claudeTrial.planQuantity || 0;
    claudeItem.finishDate = claudeTrial.finishDate || null;
    claudeItem.isConnect = isConnectionActive
  }

  if (claudeSub && (!claudeTrial || claudeTrial?.trialStatus !== ETrialStatus.STARTED || claudeSub?.status === EStripeSubStatus.ACTIVE)) {
    claudeItem.plan = claudeSub.planName || '';
    claudeItem.status = claudeSub.status;
    claudeItem.quantity = claudeSub.quantity || 0;
    claudeItem.finishDate = claudeSub.finishDate || null;
    claudeItem.planQuantity = claudeSub.planQuantity || 0;
    claudeItem.addedItemsQuantity = claudeSub.addedItemsQuantity || 0;
    claudeItem.price = `$${claudeSub.cost.toFixed(2)} / ${claudeSub.isAnnual ? 'yr.' : 'mo.'}`
    claudeItem.isCancel = claudeSub.isCancel;
    claudeItem.finishDateInMillisecond = claudeSub.finishDateInMillisecond;
    claudeItem.isConnect = isConnectionActive
  }

  return claudeItem
});
